<template>
  <div
    class="media-uploader relative"
    :class="{ 'cursor-not-allowed': disabled || isLimitReached }"
  >
    <div
      class="relative flex flex-col w-full border border-primary rounded-xl border-dashed bg-transparent text-white overflow-hidden"
      :class="{
        'ring-2 ring-primary border-primary/60': uploadState.isDragActive,
        'cursor-not-allowed': isLimitReached,
      }"
      @click="onCardClick"
      @dragenter.prevent="onDragEnter"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <!-- Loading State -->
      <div
        v-if="loading"
        class="flex justify-center items-center min-h-[140px]"
      >
        <div
          class="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"
        ></div>
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!files.length || !showPreview"
        class="flex flex-col items-center justify-center min-h-[140px] text-center gap-3 px-6 py-8 pointer-events-none select-none"
      >
        <UIcon :name="mediaIcon" class="w-6 h-6" />
        <div class="text-sm">
          <span class="text-primary font-semibold">Click to upload</span>
          or drag and drop
        </div>
        <div class="text-xs text-white/40">
          {{ formatsHint }}
        </div>
      </div>

      <!-- Single File Preview -->
      <div v-else-if="showPreview && maxFiles === 1" class="relative group">
        <!-- Image Preview -->
        <img
          v-if="mediaType === 'image'"
          :src="getPreviewUrl(files[0])"
          :alt="label || 'Image'"
          class="w-full h-auto object-contain"
          :style="{ maxHeight: maxPreviewHeight || '140px' }"
        />
        <!-- Video Preview -->
        <video
          v-else-if="mediaType === 'video'"
          class="w-full h-auto object-contain bg-black"
          :src="getPreviewUrl(files[0])"
          controls
          playsinline
          preload="metadata"
          controlsList="nodownload noremoteplayback noplaybackrate"
          disablepictureinpicture
          :style="{ maxHeight: maxPreviewHeight || '400px' }"
        >
          <source :src="getPreviewUrl(files[0])" :type="files[0].type" />
          Your browser does not support the video tag.
        </video>
        <!-- Audio Preview -->
        <div
          v-else-if="mediaType === 'audio'"
          class="flex flex-col items-center justify-center min-h-[140px] p-6"
        >
          <UIcon name="i-lucide-music" class="w-16 h-16 mb-4 text-primary" />
          <audio
            class="w-full max-w-md"
            :src="getPreviewUrl(files[0])"
            controls
            preload="metadata"
            controlsList="nodownload noremoteplayback noplaybackrate"
          >
            <source :src="getPreviewUrl(files[0])" :type="files[0].type" />
            Your browser does not support the audio tag.
          </audio>
        </div>

        <!-- Remove Button -->
        <UButton
          class="absolute top-3 right-3 w-9 h-9 rounded-full hover:bg-red-500/80 flex items-center justify-center opacity-0 group-hover:opacity-100"
          color="neutral"
          variant="outline"
          size="sm"
          title="Remove"
          @click.stop="removeFile(0)"
          icon="i-lucide-x"
        />

        <!-- File Info (for video/audio single mode) -->
        <div
          v-if="(mediaType === 'video' || mediaType === 'audio') && files[0]"
          class="p-4 border-t border-primary/30 bg-black/40 flex items-center justify-between gap-4 flex-wrap"
        >
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium truncate">{{ files[0].name }}</p>
            <p class="text-xs text-white/70 mt-1">
              {{ formatFileSize(files[0].size) }} -
              {{
                mediaType === "video"
                  ? getVideoType(files[0].type)
                  : getAudioType(files[0].type)
              }}
            </p>
          </div>
        </div>
      </div>

      <!-- Multiple Files Grid -->
      <div v-else class="flex flex-col">
        <div class="p-4">
          <UButton
            class="mb-3"
            color="neutral"
            variant="outline"
            size="sm"
            icon="i-lucide-plus"
            :disabled="isLimitReached"
          >
            Add More
          </UButton>
          <div
            class="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-3"
            :class="{
              'sm:grid-cols-3': files.length >= 4,
            }"
          >
            <div
              v-for="(file, fileIndex) in files"
              :key="`file-${fileIndex}`"
              class="relative aspect-square rounded-lg overflow-hidden bg-black/30 group max-h-[140px]"
            >
              <!-- Image Thumbnail -->
              <img
                v-if="mediaType === 'image'"
                :src="getPreviewUrl(file)"
                :alt="`${label || 'Image'} ${fileIndex + 1}`"
                class="absolute inset-0 w-full h-full object-cover"
              />
              <!-- Video Thumbnail -->
              <video
                v-else-if="mediaType === 'video'"
                :src="getPreviewUrl(file)"
                class="absolute inset-0 w-full h-full object-cover"
                preload="metadata"
                autoplay
                loop
                muted
                playsinline
              >
                <source :src="getPreviewUrl(file)" :type="file.type" />
              </video>
              <!-- Audio Thumbnail -->
              <div
                v-else-if="mediaType === 'audio'"
                class="absolute inset-0 w-full h-full flex flex-col items-center justify-center bg-linear-to-br from-primary/20 to-primary/5"
              >
                <UIcon
                  name="i-lucide-music"
                  class="w-10 h-10 text-primary mb-2"
                />
                <p
                  class="text-xs text-white/70 truncate px-2 w-full text-center"
                >
                  {{ file.name }}
                </p>
              </div>

              <!-- Remove Button -->
              <UButton
                class="absolute top-2 right-2 w-7 h-7 rounded-full hover:bg-red-500/80 opacity-0 group-hover:opacity-100"
                size="sm"
                color="neutral"
                variant="outline"
                @click.stop="removeFile(fileIndex)"
                icon="i-lucide-x"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom URL Input Area -->
      <div class="sticky bottom-0 w-full mt-auto">
        <!-- URL Toggle Button -->
        <div
          class="flex items-center justify-center px-3 h-11 border-t border-primary/50"
          @click.stop
        >
          <UButton
            class="flex items-center gap-2 text-xs"
            color="neutral"
            variant="ghost"
            size="sm"
            :disabled="isLimitReached || loading"
            @click="toggleUrlInput(true)"
            icon="i-lucide-link"
          >
            Paste
            {{
              mediaType === "image"
                ? "Image"
                : mediaType === "video"
                ? "Video"
                : "Audio"
            }}
            URL
          </UButton>
        </div>

        <!-- URL Input Panel -->
        <div
          class="absolute bottom-0 left-0 w-full h-11 bg-[#252836] border-t border-primary/50 px-4 flex items-center gap-3"
          :class="uploadState.isUrlMode ? 'translate-y-0' : 'translate-y-full'"
          @click.stop
        >
          <input
            type="text"
            class="flex-1 bg-transparent border-none outline-none text-sm text-white placeholder-white/40 min-w-0"
            :placeholder="urlPlaceholder"
            v-model="uploadState.url"
            ref="urlInputRef"
            @keydown.enter.prevent="confirmUrlInput"
            @keydown.esc.prevent="toggleUrlInput(false)"
          />
          <div class="flex items-center gap-2">
            <UButton
              color="primary"
              variant="outline"
              size="sm"
              title="Confirm"
              :disabled="!uploadState.url"
              :loading="isAddingUrl"
              @click="confirmUrlInput"
              icon="i-lucide-check"
            />
            <UButton
              color="neutral"
              variant="outline"
              size="sm"
              title="Cancel"
              @click="toggleUrlInput(false)"
              icon="i-lucide-x"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden File Input -->
    <input
      type="file"
      class="hidden"
      :accept="acceptFormats"
      :multiple="maxFiles > 1"
      ref="fileInputRef"
      @change="onFileInputChange"
    />

    <UModal
      v-model:open="cropperState.open"
      :dismissible="false"
      :ui="{
        content:
          'max-w-2xl w-[95vw] rounded-xl bg-[#141623] border border-white/10',
      }"
    >
      <template #default>
        <span class="hidden"></span>
      </template>
      <template #content>
        <div class="p-5 sm:p-6 text-white">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-xl font-semibold">Edit uploaded images</h3>
            <UButton
              color="neutral"
              variant="ghost"
              icon="i-lucide-x"
              class="text-white/70"
              @click="cancelCropper"
            />
          </div>

          <div
            class="w-full bg-black/40 rounded-lg border border-white/10 overflow-hidden"
          >
            <div
              ref="cropPreviewContainerRef"
              class="relative mx-auto w-full max-w-[680px] h-[360px] sm:h-[400px] touch-none"
              @pointerdown="onCropPointerDown"
            >
              <img
                v-if="cropperState.sourceUrl"
                :src="cropperState.sourceUrl"
                alt="crop-preview"
                class="absolute max-w-none select-none pointer-events-none"
                :class="isDraggingCrop ? 'cursor-grabbing' : 'cursor-grab'"
                :style="previewImageStyle"
                @load="onCropPreviewLoad"
                draggable="false"
              />
              <div
                class="pointer-events-none absolute border-2 border-white/80 rounded-xs shadow-[0_0_0_9999px_rgba(0,0,0,0.38)]"
                :style="cropFrameStyle"
              ></div>
            </div>
          </div>

          <div class="mt-5 flex items-center gap-3">
            <UButton
              color="neutral"
              variant="ghost"
              icon="i-lucide-minus"
              @click="cropperState.zoom = Math.max(1, cropperState.zoom - 0.1)"
            />
            <input
              v-model.number="cropperState.zoom"
              type="range"
              min="1"
              max="3"
              step="0.05"
              class="flex-1 accent-primary"
            />
            <UButton
              color="neutral"
              variant="ghost"
              icon="i-lucide-plus"
              @click="cropperState.zoom = Math.min(3, cropperState.zoom + 0.1)"
            />
          </div>

          <div
            v-if="normalizedCropRatioItems.length"
            class="mt-4 rounded-lg bg-black/20 border border-white/10 p-3"
          >
            <p class="text-sm text-white/80 mb-3">Aspect Ratio</p>
            <div class="flex flex-wrap gap-2">
              <UButton
                v-for="ratio in normalizedCropRatioItems"
                :key="ratio.value"
                class="text-white min-w-[64px] py-2 justify-center items-center text-center"
                size="sm"
                :color="
                  cropperState.selectedRatio === ratio.value
                    ? 'primary'
                    : 'neutral'
                "
                :variant="
                  cropperState.selectedRatio === ratio.value
                    ? 'solid'
                    : 'outline'
                "
                @click="selectCropRatio(ratio.value)"
              >
                <span
                  class="w-full flex flex-col items-center justify-center leading-tight gap-1"
                >
                  <UIcon v-if="ratio.icon" :name="ratio.icon" class="w-4 h-4" />
                  <span>{{ ratio.label }}</span>
                </span>
              </UButton>
            </div>
          </div>

          <div class="mt-6 flex items-center justify-between">
            <UButton
              color="neutral"
              variant="ghost"
              label="Reset"
              @click="resetCropperView"
            />
            <div class="flex items-center gap-2">
              <UButton
                color="neutral"
                variant="outline"
                label="Cancel"
                @click="cancelCropper"
              />
              <UButton color="primary" label="OK" @click="confirmCropper" />
            </div>
          </div>
        </div>
      </template>
    </UModal>
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from "vue";
import { useToast } from "#imports";
import { setFileMediaDuration } from "@/common/utils/media-duration";
import { resolveFetchAssetUrl } from "@/utils/r2Url";

// Props
const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  mediaType: {
    type: String,
    required: true,
    validator: (value) => ["image", "video", "audio"].includes(value),
  },
  maxFiles: {
    type: Number,
    default: 1,
  },
  formats: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    default: "",
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  maxPreviewHeight: {
    type: String,
    default: undefined,
  },
  showPreview: {
    type: Boolean,
    default: true,
  },
  cropRatioItems: {
    type: Array,
    default: () => [],
  },
  cropRatioValue: {
    type: String,
    default: "",
  },
  enableCropper: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits([
  "update:modelValue",
  "add",
  "remove",
  "limit-reached",
  "invalid-format",
  "update:cropRatioValue",
]);

// Toast
const toast = useToast();

// Internal State
const files = ref([...(props.modelValue || [])]);
const uploadState = reactive({
  url: "",
  isUrlMode: false,
  isDragActive: false,
  dragCounter: 0,
});
const fileInputRef = ref(null);
const urlInputRef = ref(null);
const previewUrlCache = new WeakMap();
const isAddingUrl = ref(false);
const cropperState = reactive({
  open: false,
  sourceUrl: "",
  selectedRatio: "",
  zoom: 1,
});
const pendingCropFile = ref(null);
const cropPreviewContainerRef = ref(null);
const cropPreviewSize = reactive({
  width: 0,
  height: 0,
});
const cropPreviewImageMeta = reactive({
  naturalWidth: 0,
  naturalHeight: 0,
});
const cropOffset = reactive({
  x: 0,
  y: 0,
});
const cropDragState = reactive({
  active: false,
  startX: 0,
  startY: 0,
  startOffsetX: 0,
  startOffsetY: 0,
});

const DEFAULT_CROP_RATIO_ITEMS = [
  {
    label: "Match Input Image",
    value: "match_input_image",
    icon: "i-ratio-match",
  },
  { label: "1:1", value: "1:1", icon: "i-ratio-1-1" },
  { label: "1:4", value: "1:4", icon: "i-ratio-1-4" },
  { label: "4:1", value: "4:1", icon: "i-ratio-4-1" },
  { label: "1:8", value: "1:8", icon: "i-ratio-1-8" },
  { label: "8:1", value: "8:1", icon: "i-ratio-8-1" },
  { label: "2:3", value: "2:3", icon: "i-ratio-2-3" },
  { label: "3:2", value: "3:2", icon: "i-ratio-3-2" },
  { label: "3:4", value: "3:4", icon: "i-ratio-3-4" },
  { label: "4:3", value: "4:3", icon: "i-ratio-4-3" },
  { label: "4:5", value: "4:5", icon: "i-ratio-4-5" },
  { label: "5:4", value: "5:4", icon: "i-ratio-5-4" },
  { label: "9:16", value: "9:16", icon: "i-ratio-9-16" },
  { label: "16:9", value: "16:9", icon: "i-ratio-16-9" },
  { label: "9:21", value: "9:21", icon: "i-ratio-9-21" },
  { label: "21:9", value: "21:9", icon: "i-ratio-21-9" },
];

/** 与 DEFAULT_CROP_RATIO_ITEMS 一致，用于自定义 cropRatioItems 时统一排序 */
const CROP_RATIO_VALUE_ORDER = new Map(
  DEFAULT_CROP_RATIO_ITEMS.map((item, i) => [item.value, i])
);

function isMatchInputCropItem(item) {
  if (!item || typeof item.value !== "string") return false;
  const v = item.value.toLowerCase();
  if (v === "match_input_image" || v.includes("match_input")) return true;
  if (item.label && /match\s*input/i.test(String(item.label))) return true;
  return false;
}

// Computed Properties
const isLimitReached = computed(() => files.value.length >= props.maxFiles);

const normalizedFormats = computed(() => normalizeFormats(props.formats));

const mediaIcon = computed(() => {
  if (props.mediaType === "image") return "i-lucide-image";
  if (props.mediaType === "video") return "i-lucide-video";
  return "i-lucide-music";
});

const formatsHint = computed(() => {
  if (normalizedFormats.value.length) {
    return normalizedFormats.value.map((f) => f.toUpperCase()).join(", ");
  }
  // if (props.mediaType === "image") return "PNG, JPG";
  // if (props.mediaType === "video") return "MP4, MOV, AVI";
  return "";
});

const acceptFormats = computed(() => {
  if (normalizedFormats.value.length) {
    return normalizedFormats.value.map((f) => `.${f}`).join(", ");
  }
  // if (props.mediaType === "image") return ".png,.jpg,.jpeg";
  // if (props.mediaType === "video") return ".mp4,.mov,.avi,.wmv,.flv,.mkv,.webm";
  return "";
});

const urlPlaceholder = computed(() => {
  if (props.mediaType === "image") return "https://example.com/image.png";
  if (props.mediaType === "video") return "https://example.com/video.mp4";
  return "https://example.com/audio.mp3";
});

const hasCustomCropRatioItems = computed(() => {
  return Array.isArray(props.cropRatioItems) && props.cropRatioItems.length > 0;
});

const normalizedCropRatioItems = computed(() => {
  const baseItems = hasCustomCropRatioItems.value
    ? props.cropRatioItems || []
    : DEFAULT_CROP_RATIO_ITEMS;
  const normalized = baseItems
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const value = String(item.value ?? item.key ?? "");
      if (!value) return null;
      return {
        label: String(item.label ?? value),
        value,
        icon: item.icon ? String(item.icon) : "",
      };
    })
    .filter(Boolean);
  const UNKNOWN_ORDER = 10000;
  return [...normalized].sort((a, b) => {
    const aMatch = isMatchInputCropItem(a);
    const bMatch = isMatchInputCropItem(b);
    if (aMatch !== bMatch) return aMatch ? -1 : 1;
    const ai = CROP_RATIO_VALUE_ORDER.get(a.value) ?? UNKNOWN_ORDER;
    const bi = CROP_RATIO_VALUE_ORDER.get(b.value) ?? UNKNOWN_ORDER;
    if (ai !== bi) return ai - bi;
    return a.value.localeCompare(b.value);
  });
});

const shouldUseCropper = computed(() => {
  return props.enableCropper && props.mediaType === "image";
});

const previewAspectRatio = computed(() => {
  const parsed = parseRatioValue(cropperState.selectedRatio);
  if (parsed) return parsed;

  // 非 "w:h" 的 ratio（例如 Match Input Images）使用当前图片原始比例
  const iw = cropPreviewImageMeta.naturalWidth;
  const ih = cropPreviewImageMeta.naturalHeight;
  if (iw > 0 && ih > 0) {
    return iw / ih;
  }

  return 4 / 3;
});

const isDraggingCrop = computed(() => cropDragState.active);

const renderedImageRect = computed(() => {
  const cw = cropPreviewSize.width;
  const ch = cropPreviewSize.height;
  const iw = cropPreviewImageMeta.naturalWidth;
  const ih = cropPreviewImageMeta.naturalHeight;

  if (!cw || !ch) {
    return { left: 0, top: 0, width: 0, height: 0 };
  }
  if (!iw || !ih) {
    return { left: 0, top: 0, width: cw, height: ch };
  }

  const imageRatio = iw / ih;
  const containerRatio = cw / ch;

  if (containerRatio > imageRatio) {
    const height = ch;
    const width = height * imageRatio;
    return {
      left: (cw - width) / 2,
      top: 0,
      width,
      height,
    };
  }

  const width = cw;
  const height = width / imageRatio;
  return {
    left: 0,
    top: (ch - height) / 2,
    width,
    height,
  };
});

const cropFrameRect = computed(() => {
  const ratio = previewAspectRatio.value || 1;
  const imageRect = renderedImageRect.value;
  if (!imageRect.width || !imageRect.height) {
    return { left: 0, top: 0, width: 0, height: 0 };
  }

  let width = imageRect.width;
  let height = width / ratio;

  if (height > imageRect.height) {
    height = imageRect.height;
    width = height * ratio;
  }

  return {
    left: imageRect.left + (imageRect.width - width) / 2,
    top: imageRect.top + (imageRect.height - height) / 2,
    width,
    height,
  };
});

const cropFrameStyle = computed(() => {
  const rect = cropFrameRect.value;
  if (!rect.width || !rect.height) return { display: "none" };
  return {
    left: `${rect.left}px`,
    top: `${rect.top}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
  };
});

const previewImageLayout = computed(() => {
  const imageRect = renderedImageRect.value;
  const frameRect = cropFrameRect.value;
  const zoom =
    Number.isFinite(cropperState.zoom) && cropperState.zoom > 0
      ? cropperState.zoom
      : 1;
  const scaledWidth = imageRect.width * zoom;
  const scaledHeight = imageRect.height * zoom;
  const baseLeft = imageRect.left + (imageRect.width - scaledWidth) / 2;
  const baseTop = imageRect.top + (imageRect.height - scaledHeight) / 2;

  if (!scaledWidth || !scaledHeight || !frameRect.width || !frameRect.height) {
    return {
      left: baseLeft,
      top: baseTop,
      width: scaledWidth,
      height: scaledHeight,
      minOffsetX: 0,
      maxOffsetX: 0,
      minOffsetY: 0,
      maxOffsetY: 0,
    };
  }

  return {
    left: baseLeft,
    top: baseTop,
    width: scaledWidth,
    height: scaledHeight,
    minOffsetX: frameRect.left + frameRect.width - (baseLeft + scaledWidth),
    maxOffsetX: frameRect.left - baseLeft,
    minOffsetY: frameRect.top + frameRect.height - (baseTop + scaledHeight),
    maxOffsetY: frameRect.top - baseTop,
  };
});

const previewImageStyle = computed(() => {
  const layout = previewImageLayout.value;
  return {
    left: `${layout.left + cropOffset.x}px`,
    top: `${layout.top + cropOffset.y}px`,
    width: `${layout.width}px`,
    height: `${layout.height}px`,
  };
});

const updateCropPreviewSize = () => {
  const el = cropPreviewContainerRef.value;
  if (!el) return;
  cropPreviewSize.width = el.clientWidth || 0;
  cropPreviewSize.height = el.clientHeight || 0;
};

const onCropPreviewLoad = (event) => {
  const img = event?.target;
  if (!img) return;
  cropPreviewImageMeta.naturalWidth = img.naturalWidth || 0;
  cropPreviewImageMeta.naturalHeight = img.naturalHeight || 0;
  nextTick(() => updateCropPreviewSize());
};

const clamp = (value, minValue, maxValue) => {
  if (!Number.isFinite(value)) return 0;
  if (!Number.isFinite(minValue) || !Number.isFinite(maxValue)) return value;
  if (minValue > maxValue) {
    return (minValue + maxValue) / 2;
  }
  return Math.min(maxValue, Math.max(minValue, value));
};

const clampCropOffset = (x, y) => {
  const layout = previewImageLayout.value;
  return {
    x: clamp(x, layout.minOffsetX, layout.maxOffsetX),
    y: clamp(y, layout.minOffsetY, layout.maxOffsetY),
  };
};

const applyCropOffset = (x, y) => {
  const clamped = clampCropOffset(x, y);
  cropOffset.x = clamped.x;
  cropOffset.y = clamped.y;
};

const onCropPointerMove = (event) => {
  if (!cropDragState.active) return;
  event.preventDefault();
  const nextX =
    cropDragState.startOffsetX + (event.clientX - cropDragState.startX);
  const nextY =
    cropDragState.startOffsetY + (event.clientY - cropDragState.startY);
  applyCropOffset(nextX, nextY);
};

const onCropPointerUp = () => {
  if (!cropDragState.active) return;
  cropDragState.active = false;
  if (typeof window !== "undefined") {
    window.removeEventListener("pointermove", onCropPointerMove);
    window.removeEventListener("pointerup", onCropPointerUp);
    window.removeEventListener("pointercancel", onCropPointerUp);
  }
};

const onCropPointerDown = (event) => {
  if (!cropperState.sourceUrl) return;
  if (event.button !== 0) return;
  event.preventDefault();
  cropDragState.active = true;
  cropDragState.startX = event.clientX;
  cropDragState.startY = event.clientY;
  cropDragState.startOffsetX = cropOffset.x;
  cropDragState.startOffsetY = cropOffset.y;
  if (typeof window !== "undefined") {
    window.addEventListener("pointermove", onCropPointerMove);
    window.addEventListener("pointerup", onCropPointerUp);
    window.addEventListener("pointercancel", onCropPointerUp);
  }
};

const resetCropperView = () => {
  cropperState.zoom = 1;
  cropOffset.x = 0;
  cropOffset.y = 0;
};

const selectCropRatio = (ratioValue) => {
  cropperState.selectedRatio = ratioValue;
  applyCropOffset(cropOffset.x, cropOffset.y);
};

// 使用标志位防止循环更新
let isInternalUpdate = false;

// Watch for external changes
watch(
  () => props.modelValue,
  (newVal) => {
    if (isInternalUpdate) return; // 如果是内部触发的更新，跳过

    const newFiles = newVal && Array.isArray(newVal) ? newVal : [];

    // 外部更新 modelValue 时，释放被移除文件的预览 URL，避免内存泄漏
    const removedFiles = (files.value || []).filter(
      (oldFile) => !newFiles.includes(oldFile)
    );
    removedFiles.forEach((file) => releasePreviewUrl(file));

    files.value = [...newFiles];
  },
  { deep: true }
);

// Emit changes
watch(
  files,
  (newVal) => {
    isInternalUpdate = true; // 标记为内部更新
    emit("update:modelValue", [...newVal]);
    nextTick(() => {
      isInternalUpdate = false; // 下一个 tick 后重置标志
    });
  },
  { deep: true }
);

watch(
  () => props.cropRatioValue,
  (newVal) => {
    if (!newVal || cropperState.open) return;
    cropperState.selectedRatio = newVal;
  },
  { immediate: true }
);

watch(
  () => [
    cropperState.zoom,
    cropPreviewSize.width,
    cropPreviewSize.height,
    cropPreviewImageMeta.naturalWidth,
    cropPreviewImageMeta.naturalHeight,
    cropFrameRect.value.width,
    cropFrameRect.value.height,
    cropFrameRect.value.left,
    cropFrameRect.value.top,
  ],
  () => {
    applyCropOffset(cropOffset.x, cropOffset.y);
  }
);

// Utility Functions
function addExtension(setRef, ext) {
  if (!ext) return;
  setRef.add(ext);
  if (ext === "jpg") {
    setRef.add("jpeg");
  }
  if (ext === "jpeg") {
    setRef.add("jpg");
  }
}

const normalizeFormats = (formats = []) => {
  const formatSet = new Set();
  formats.forEach((format) => {
    if (!format) return;
    const normalized = format.toLowerCase();
    if (normalized.includes("/")) {
      const ext = normalized.split("/").pop();
      addExtension(formatSet, ext);
    } else {
      const clean = normalized.startsWith(".")
        ? normalized.slice(1)
        : normalized;
      addExtension(formatSet, clean);
    }
  });
  return Array.from(formatSet);
};

const filterFilesByFormats = (filesToFilter, allowed = []) => {
  if (!allowed.length) return filesToFilter;
  return filesToFilter.filter((file) => {
    const fileExt = file.name?.split(".").pop()?.toLowerCase();
    const mimeExt = file.type?.split("/").pop()?.toLowerCase();
    return allowed.includes(fileExt) || allowed.includes(mimeExt);
  });
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const getVideoType = (mimeType) => {
  const types = {
    "video/mp4": "MP4",
    "video/quicktime": "MOV",
    "video/x-msvideo": "AVI",
    "video/x-ms-wmv": "WMV",
    "video/x-flv": "FLV",
    "video/x-matroska": "MKV",
    "video/webm": "WEBM",
  };
  return (
    types[mimeType] || mimeType?.split("/").pop()?.toUpperCase() || "VIDEO"
  );
};

const getAudioType = (mimeType) => {
  const types = {
    "audio/mpeg": "MP3",
    "audio/wav": "WAV",
    "audio/x-m4a": "M4A",
    "audio/mp4": "M4A",
    "audio/aac": "AAC",
    "audio/ogg": "OGG",
    "audio/flac": "FLAC",
  };
  return (
    types[mimeType] || mimeType?.split("/").pop()?.toUpperCase() || "AUDIO"
  );
};

// Drag & Drop Handlers
const onDragEnter = (event) => {
  if (props.disabled || isLimitReached.value || props.loading) return;
  uploadState.dragCounter += 1;
  uploadState.isDragActive = true;
};

const onDragOver = (event) => {
  if (props.disabled || isLimitReached.value || props.loading) return;
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = "copy";
  }
};

const onDragLeave = (event) => {
  uploadState.dragCounter = Math.max(0, uploadState.dragCounter - 1);
  if (uploadState.dragCounter === 0) {
    uploadState.isDragActive = false;
  }
};

const onDrop = (event) => {
  uploadState.dragCounter = 0;
  uploadState.isDragActive = false;
  if (props.disabled || isLimitReached.value || props.loading) return;
  const droppedFiles = Array.from(event.dataTransfer?.files || []);
  void addFiles(droppedFiles);
};

// File Operations
const addFiles = async (filesToAdd) => {
  if (!filesToAdd.length) return;

  const allowedFormats = normalizeFormats(props.formats);
  const validFiles = filterFilesByFormats(filesToAdd, allowedFormats);
  const invalidCount = filesToAdd.length - validFiles.length;

  if (invalidCount > 0) {
    const invalidFiles = filesToAdd.filter((f) => !validFiles.includes(f));
    emit("invalid-format", invalidFiles);
    toast.add({
      title: "Some files were skipped",
      description: `Please upload supported ${props.mediaType} formats.`,
      color: "warning",
      icon: "i-lucide-alert-triangle",
    });
  }

  if (!validFiles.length) {
    return;
  }

  const availableSlots = props.maxFiles - files.value.length;
  if (availableSlots <= 0) {
    emit("limit-reached");
    toast.add({
      title: `${
        props.mediaType === "image"
          ? "Image"
          : props.mediaType === "video"
          ? "Video"
          : "Audio"
      } limit reached`,
      description: `Remove an existing ${props.mediaType} before adding a new one.`,
      color: "warning",
      icon: "i-lucide-alert-triangle",
    });
    return;
  }

  const filesToAddSliced = validFiles.slice(0, availableSlots);
  const preparedFiles = await prepareFilesBeforeAdd(filesToAddSliced);
  if (!preparedFiles.length) return;
  files.value.push(...preparedFiles);
  emit("add", preparedFiles);
};

const removeFile = (index) => {
  if (index < 0 || index >= files.value.length) return;
  const [removed] = files.value.splice(index, 1);
  releasePreviewUrl(removed);
  emit("remove", index);
};

// URL Operations
const toggleUrlInput = (active) => {
  if (props.disabled || props.loading) return;
  uploadState.isUrlMode = active;
  if (!active) {
    uploadState.url = "";
  } else {
    nextTick(() => {
      urlInputRef.value?.focus();
    });
  }
};

const confirmUrlInput = async () => {
  const url = uploadState.url.trim();
  if (!url) return;

  if (isLimitReached.value) {
    emit("limit-reached");
    toast.add({
      title: `${
        props.mediaType === "image"
          ? "Image"
          : props.mediaType === "video"
          ? "Video"
          : "Audio"
      } limit reached`,
      description: `Remove an existing ${props.mediaType} before adding a new one.`,
      color: "warning",
      icon: "i-lucide-alert-triangle",
    });
    return;
  }

  isAddingUrl.value = true;
  try {
    const allowedFormats = normalizedFormats.value;
    const formatsForUrl = allowedFormats.length
      ? allowedFormats
      : props.formats;
    const file = await urlToFile(url, formatsForUrl);
    const [preparedFile] = await prepareFilesBeforeAdd([file]);
    if (!preparedFile) return;
    files.value.push(preparedFile);
    emit("add", [preparedFile]);
    toast.add({
      title: "Success",
      color: "success",
      icon: "i-lucide-check-circle",
      description: "File added successfully",
    });
  } catch (error) {
    console.error("Failed to add from URL:", error);
    toast.add({
      title: "Failed to add file",
      description: error.message || "Could not download file from URL",
      color: "error",
      icon: "i-lucide-alert-triangle",
    });
  } finally {
    uploadState.url = "";
    uploadState.isUrlMode = false;
    isAddingUrl.value = false;
  }
};

// Preview Management
const getPreviewUrl = (file) => {
  if (!file || typeof URL === "undefined") return "";
  if (!previewUrlCache.has(file)) {
    previewUrlCache.set(file, URL.createObjectURL(file));
  }
  return previewUrlCache.get(file);
};

const releasePreviewUrl = (file) => {
  if (!file || typeof URL === "undefined") return;
  const cached = previewUrlCache.get(file);
  if (cached) {
    URL.revokeObjectURL(cached);
    previewUrlCache.delete(file);
  }
};

// File Input Handlers
const onCardClick = () => {
  if (
    props.disabled ||
    isLimitReached.value ||
    uploadState.isUrlMode ||
    props.loading
  )
    return;
  fileInputRef.value?.click();
};

const onFileInputChange = (event) => {
  const selectedFiles = Array.from(event.target?.files || []);
  void addFiles(selectedFiles);
  if (event.target) {
    event.target.value = "";
  }
};

function parseRatioValue(ratioValue) {
  if (!ratioValue || typeof ratioValue !== "string") return null;
  const [w, h] = ratioValue.split(":").map(Number);
  if (!w || !h || Number.isNaN(w) || Number.isNaN(h)) return null;
  return w / h;
}

const getDefaultCropRatio = () => {
  const candidate = hasCustomCropRatioItems.value
    ? props.cropRatioValue || normalizedCropRatioItems.value[0]?.value
    : normalizedCropRatioItems.value[0]?.value;
  if (!candidate) return "";
  const exists = normalizedCropRatioItems.value.some(
    (item) => item.value === candidate
  );
  return exists ? candidate : normalizedCropRatioItems.value[0]?.value || "";
};

const openCropper = (file) => {
  return new Promise((resolve) => {
    if (!file || !shouldUseCropper.value) {
      resolve(file);
      return;
    }
    pendingCropFile.value = file;
    cropperState.sourceUrl = URL.createObjectURL(file);
    cropperState.selectedRatio = getDefaultCropRatio();
    cropperState.zoom = 1;
    cropOffset.x = 0;
    cropOffset.y = 0;
    cropPreviewImageMeta.naturalWidth = 0;
    cropPreviewImageMeta.naturalHeight = 0;
    cropperState.open = true;
    cropperState._resolve = resolve;
    nextTick(() => updateCropPreviewSize());
  });
};

const closeCropper = (resultFile) => {
  const resolver = cropperState._resolve;
  const sourceUrl = cropperState.sourceUrl;
  onCropPointerUp();
  cropperState.open = false;
  cropperState.sourceUrl = "";
  cropperState.zoom = 1;
  cropOffset.x = 0;
  cropOffset.y = 0;
  cropperState._resolve = null;
  pendingCropFile.value = null;
  cropPreviewImageMeta.naturalWidth = 0;
  cropPreviewImageMeta.naturalHeight = 0;
  if (sourceUrl) {
    URL.revokeObjectURL(sourceUrl);
  }
  if (typeof resolver === "function") {
    resolver(resultFile || null);
  }
};

const confirmCropper = async () => {
  try {
    const originalFile = pendingCropFile.value;
    if (!originalFile) {
      closeCropper(null);
      return;
    }

    const croppedFile = await cropImageFile(
      originalFile,
      cropperState.selectedRatio,
      cropperState.zoom,
      {
        offsetX: cropOffset.x,
        offsetY: cropOffset.y,
        frameRect: cropFrameRect.value,
        renderedRect: renderedImageRect.value,
      }
    );

    if (hasCustomCropRatioItems.value && cropperState.selectedRatio) {
      emit("update:cropRatioValue", cropperState.selectedRatio);
    }
    closeCropper(croppedFile);
  } catch (error) {
    console.error("Crop failed:", error);
    toast.add({
      title: "Crop failed",
      description: "Unable to crop image, please try again.",
      color: "error",
      icon: "i-lucide-alert-triangle",
    });
    closeCropper(null);
  }
};

const cancelCropper = () => {
  closeCropper(null);
};

const prepareFilesBeforeAdd = async (incomingFiles) => {
  if (!Array.isArray(incomingFiles) || incomingFiles.length === 0) return [];
  const prepared = [];

  for (const file of incomingFiles) {
    let nextFile = file;

    if (shouldUseCropper.value) {
      nextFile = await openCropper(file);
      if (!nextFile) {
        continue;
      }
    }

    nextFile = await enrichMediaFileMetadata(nextFile);
    prepared.push(nextFile);
  }
  return prepared;
};

const enrichMediaFileMetadata = async (file) => {
  if (!file || !["video", "audio"].includes(props.mediaType)) {
    return file;
  }

  try {
    const duration = await readMediaDurationFromFile(file, props.mediaType);
    setFileMediaDuration(file, duration);
  } catch (error) {
    console.warn("[MediaUploader] Failed to read media duration", {
      name: file?.name,
      mediaType: props.mediaType,
      error: error?.message || error,
    });
  }

  return file;
};

const readMediaDurationFromFile = (file, mediaType = "video") => {
  return new Promise((resolve, reject) => {
    if (typeof document === "undefined" || typeof URL === "undefined") {
      reject(new Error("Media duration detection is unavailable"));
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const media = document.createElement(
      mediaType === "audio" ? "audio" : "video"
    );

    let settled = false;

    const cleanup = () => {
      media.removeEventListener("loadedmetadata", handleDurationReady);
      media.removeEventListener("durationchange", handleDurationReady);
      media.removeEventListener("error", handleError);
      media.pause?.();
      media.removeAttribute("src");
      media.load?.();
      URL.revokeObjectURL(objectUrl);
    };

    const resolveOnce = (duration) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(duration);
    };

    const rejectOnce = (error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };

    const handleDurationReady = () => {
      const duration = Number(media.duration);
      if (Number.isFinite(duration) && duration > 0) {
        resolveOnce(duration);
      }
    };

    const handleError = () => {
      rejectOnce(new Error("Failed to load media metadata"));
    };

    media.preload = "metadata";
    media.muted = true;
    media.playsInline = true;
    media.addEventListener("loadedmetadata", handleDurationReady);
    media.addEventListener("durationchange", handleDurationReady);
    media.addEventListener("error", handleError, { once: true });
    media.src = objectUrl;
    media.load();
  });
};

const loadImageFromFile = (file) => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };
    img.onerror = async () => {
      URL.revokeObjectURL(objectUrl);

      if (typeof createImageBitmap === "function") {
        try {
          const bitmap = await createImageBitmap(file);
          resolve(bitmap);
          return;
        } catch (bitmapError) {
          reject(
            new Error(
              `Image decode failed. createImageBitmap failed: ${
                bitmapError?.message || "Unknown error"
              }`
            )
          );
          return;
        }
      }

      reject(new Error("Image decode failed"));
    };
    img.src = objectUrl;
  });
};

const URL_FILE_MIME_MAP = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  avif: "image/avif",
  mp4: "video/mp4",
  mov: "video/quicktime",
  webm: "video/webm",
  avi: "video/x-msvideo",
  mkv: "video/x-matroska",
  wmv: "video/x-ms-wmv",
  mp3: "audio/mpeg",
  wav: "audio/wav",
  m4a: "audio/mp4",
  ogg: "audio/ogg",
  flac: "audio/flac",
  aac: "audio/aac",
};

const MIME_EXTENSION_MAP = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/avif": "avif",
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/webm": "webm",
  "video/x-msvideo": "avi",
  "video/x-matroska": "mkv",
  "video/x-ms-wmv": "wmv",
  "audio/mpeg": "mp3",
  "audio/wav": "wav",
  "audio/mp4": "m4a",
  "audio/ogg": "ogg",
  "audio/flac": "flac",
  "audio/aac": "aac",
};

const OPAQUE_BINARY_EXTENSIONS = new Set(["octet-stream", "bin", "dat"]);

const getExtensionFromUrl = (url) => {
  try {
    const path = new URL(url).pathname;
    const fileName = path.split("/").pop() || "";
    const ext = fileName.includes(".") ? fileName.split(".").pop() : "";
    return ext?.toLowerCase() || "";
  } catch (error) {
    console.warn("URL path parsing failed");
    return "";
  }
};

const getFileNameFromUrl = (url, fallbackExt = "") => {
  let fileName = `file_${Date.now()}`;
  try {
    const decodedPath = decodeURIComponent(new URL(url).pathname);
    const extractedName = decodedPath.split("/").pop();
    if (extractedName && extractedName.includes(".")) {
      return extractedName;
    }
  } catch (error) {}

  if (fallbackExt) {
    return `${fileName}.${fallbackExt}`;
  }
  return fileName;
};

const replaceFileExtension = (fileName, nextExt) => {
  const safeName = fileName || `file_${Date.now()}`;
  const baseName = safeName.includes(".")
    ? safeName.replace(/\.[^.]+$/, "")
    : safeName;
  return `${baseName}.${nextExt}`;
};

const getPreferredImageOutputType = (formats = []) => {
  const supportedOutputTypes = {
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    webp: "image/webp",
  };

  for (const format of normalizeFormats(formats)) {
    if (supportedOutputTypes[format]) {
      return supportedOutputTypes[format];
    }
  }

  return "image/png";
};

const detectImageMimeFromBlob = async (blob) => {
  try {
    const buffer = await blob.slice(0, 32).arrayBuffer();
    const bytes = new Uint8Array(buffer);

    if (
      bytes.length >= 8 &&
      bytes[0] === 0x89 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x4e &&
      bytes[3] === 0x47 &&
      bytes[4] === 0x0d &&
      bytes[5] === 0x0a &&
      bytes[6] === 0x1a &&
      bytes[7] === 0x0a
    ) {
      return "image/png";
    }

    if (
      bytes.length >= 3 &&
      bytes[0] === 0xff &&
      bytes[1] === 0xd8 &&
      bytes[2] === 0xff
    ) {
      return "image/jpeg";
    }

    if (
      bytes.length >= 6 &&
      bytes[0] === 0x47 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x38 &&
      (bytes[4] === 0x37 || bytes[4] === 0x39) &&
      bytes[5] === 0x61
    ) {
      return "image/gif";
    }

    if (
      bytes.length >= 12 &&
      bytes[0] === 0x52 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x46 &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50
    ) {
      return "image/webp";
    }

    if (
      bytes.length >= 12 &&
      bytes[4] === 0x66 &&
      bytes[5] === 0x74 &&
      bytes[6] === 0x79 &&
      bytes[7] === 0x70 &&
      bytes[8] === 0x61 &&
      bytes[9] === 0x76 &&
      bytes[10] === 0x69 &&
      bytes[11] === 0x66
    ) {
      return "image/avif";
    }
  } catch (error) {
    console.warn("Image signature detection failed", error);
  }

  return "";
};

const getBlobHeaderHex = async (blob, byteLength = 16) => {
  try {
    const buffer = await blob.slice(0, byteLength).arrayBuffer();
    return Array.from(new Uint8Array(buffer))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join(" ");
  } catch (error) {
    return `unavailable: ${error.message}`;
  }
};

const convertBlobToImageFile = async (
  blob,
  fileName,
  outputType = "image/png",
  sourceType = ""
) => {
  const sourceFile = new File([blob], fileName || `file_${Date.now()}`, {
    type: sourceType || blob.type || "application/octet-stream",
    lastModified: Date.now(),
  });
  const image = await loadImageFromFile(sourceFile);
  const canvas = document.createElement("canvas");
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas not supported");
  }

  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

  const normalizedBlob = await new Promise((resolve, reject) => {
    canvas.toBlob((resultBlob) => {
      if (!resultBlob) {
        reject(new Error("Image conversion failed"));
        return;
      }
      resolve(resultBlob);
    }, outputType);
  });

  const finalType = normalizedBlob.type || outputType;
  const finalExt = MIME_EXTENSION_MAP[finalType] || "png";
  return new File(
    [normalizedBlob],
    replaceFileExtension(fileName || `file_${Date.now()}`, finalExt),
    {
      type: finalType,
      lastModified: Date.now(),
    }
  );
};

const cropImageFile = async (file, ratioValue, zoom = 1, cropContext = {}) => {
  const image = await loadImageFromFile(file);
  const ratio = parseRatioValue(ratioValue) || image.width / image.height;
  const safeZoom = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  const renderedRect = cropContext?.renderedRect || {};
  const frameRect = cropContext?.frameRect || {};
  const offsetX = Number(cropContext?.offsetX) || 0;
  const offsetY = Number(cropContext?.offsetY) || 0;

  const renderedWidth = Number(renderedRect.width) || 0;
  const renderedHeight = Number(renderedRect.height) || 0;
  const renderedLeft = Number(renderedRect.left) || 0;
  const renderedTop = Number(renderedRect.top) || 0;

  const frameWidth = Number(frameRect.width) || 0;
  const frameHeight = Number(frameRect.height) || 0;
  const frameLeft = Number(frameRect.left) || 0;
  const frameTop = Number(frameRect.top) || 0;

  let sourceWidth = image.width;
  let sourceHeight = image.height;
  let sx = 0;
  let sy = 0;

  if (
    renderedWidth > 0 &&
    renderedHeight > 0 &&
    frameWidth > 0 &&
    frameHeight > 0
  ) {
    const scaledWidth = renderedWidth * safeZoom;
    const scaledHeight = renderedHeight * safeZoom;
    const imageDisplayLeft =
      renderedLeft + (renderedWidth - scaledWidth) / 2 + offsetX;
    const imageDisplayTop =
      renderedTop + (renderedHeight - scaledHeight) / 2 + offsetY;

    const xScale = image.width / scaledWidth;
    const yScale = image.height / scaledHeight;

    sourceWidth = Math.max(1, Math.round(frameWidth * xScale));
    sourceHeight = Math.max(1, Math.round(frameHeight * yScale));
    sx = Math.round((frameLeft - imageDisplayLeft) * xScale);
    sy = Math.round((frameTop - imageDisplayTop) * yScale);
  } else {
    let baseWidth = image.width;
    let baseHeight = image.height;
    if (image.width / image.height > ratio) {
      baseHeight = image.height;
      baseWidth = image.height * ratio;
    } else {
      baseWidth = image.width;
      baseHeight = image.width / ratio;
    }
    sourceWidth = Math.max(1, Math.round(baseWidth / safeZoom));
    sourceHeight = Math.max(1, Math.round(baseHeight / safeZoom));
    sx = Math.round((image.width - sourceWidth) / 2);
    sy = Math.round((image.height - sourceHeight) / 2);
  }

  sx = clamp(sx, 0, image.width - sourceWidth);
  sy = clamp(sy, 0, image.height - sourceHeight);

  const canvas = document.createElement("canvas");
  canvas.width = sourceWidth;
  canvas.height = sourceHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas not supported");
  }
  ctx.drawImage(
    image,
    sx,
    sy,
    sourceWidth,
    sourceHeight,
    0,
    0,
    sourceWidth,
    sourceHeight
  );

  const outputType = file.type || "image/png";
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((resultBlob) => {
      if (!resultBlob) {
        reject(new Error("Canvas export failed"));
        return;
      }
      resolve(resultBlob);
    }, outputType);
  });

  return new File([blob], file.name, {
    type: blob.type || outputType,
    lastModified: Date.now(),
  });
};

onMounted(() => {
  if (typeof window !== "undefined") {
    window.addEventListener("resize", updateCropPreviewSize);
  }
});

// URL to File Utility (extracted from GenerativePanel)
/**
 * 终极版：从URL下载并转换为File对象
 * 特点：取消盲目兜底、强化MIME识别、支持全媒体格式
 */
async function urlToFile(url, formats = []) {
  try {
    let fetchUrl = resolveFetchAssetUrl(url);
    const currentOrigin = window.location.origin;

    // 1. 跨域代理处理
    if (fetchUrl.startsWith("http") && !fetchUrl.includes(currentOrigin)) {
      fetchUrl = `/api/proxy?url=${encodeURIComponent(fetchUrl)}`;
    }

    const response = await fetch(fetchUrl);
    if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

    const blob = await response.blob();
    const responseContentType =
      response.headers.get("content-type") ||
      response.headers.get("Content-Type") ||
      "";
    let fileExt = getExtensionFromUrl(url);
    let fileName = getFileNameFromUrl(url, fileExt);
    let finalBlob = blob;
    let finalType = blob.type;
    const hasOpaqueBinaryExtension = OPAQUE_BINARY_EXTENSIONS.has(fileExt);
    const detectedImageMime =
      props.mediaType === "image" &&
      (hasOpaqueBinaryExtension ||
        !finalType ||
        finalType === "application/octet-stream")
        ? await detectImageMimeFromBlob(blob)
        : "";
    const blobHeaderHex = await getBlobHeaderHex(blob);

    console.log("[MediaUploader:urlToFile] Fetch result", {
      originalUrl: url,
      fetchUrl,
      responseStatus: response.status,
      responseContentType,
      blobType: blob.type,
      blobSize: blob.size,
      fileExt,
      fileName,
      hasOpaqueBinaryExtension,
      detectedImageMime,
      blobHeaderHex,
    });

    // Image uploads should be normalized to a real image format instead of
    // keeping an opaque binary MIME type or an ".octet-stream" style filename.
    if (
      props.mediaType === "image" &&
      (hasOpaqueBinaryExtension ||
        !finalType ||
        finalType === "application/octet-stream")
    ) {
      try {
        const normalizedImageFile = await convertBlobToImageFile(
          blob,
          fileName,
          hasOpaqueBinaryExtension
            ? "image/png"
            : getPreferredImageOutputType(formats),
          detectedImageMime
        );
        console.log("[MediaUploader:urlToFile] Image normalized", {
          normalizedName: normalizedImageFile.name,
          normalizedType: normalizedImageFile.type,
          normalizedSize: normalizedImageFile.size,
        });
        finalBlob = normalizedImageFile;
        finalType = normalizedImageFile.type;
        fileName = normalizedImageFile.name;
        fileExt = fileName.split(".").pop()?.toLowerCase() || "";
      } catch (error) {
        console.error("[MediaUploader:urlToFile] Image normalization failed", {
          errorMessage: error?.message || "",
          responseContentType,
          blobType: blob.type,
          blobSize: blob.size,
          fileExt,
          fileName,
          detectedImageMime,
          blobHeaderHex,
        });
        throw new Error(
          "The provided URL did not return a valid image file."
        );
      }
    }

    if (!finalType || finalType === "application/octet-stream") {
      if (detectedImageMime) {
        finalType = detectedImageMime;
      } else if (fileExt && URL_FILE_MIME_MAP[fileExt]) {
        finalType = URL_FILE_MIME_MAP[fileExt];
      }
    }

    // 4. 格式校验 (严格模式)
    if (formats.length > 0) {
      const lowerFormats = formats.map((f) => f.toLowerCase());
      // 校验依据：后缀匹配 OR MIME类型中包含关键字
      const isExtValid = lowerFormats.includes(fileExt);
      const isMimeValid = lowerFormats.some((fmt) =>
        finalType.toLowerCase().includes(fmt)
      );

      if (!isExtValid && !isMimeValid) {
        throw new Error(
          `[Format Blocked] Detected: ${finalType}, Allowed: ${formats.join(
            ","
          )}`
        );
      }
    }

    if (!fileName.includes(".") && MIME_EXTENSION_MAP[finalType]) {
      fileName = `${fileName}.${MIME_EXTENSION_MAP[finalType]}`;
    }

    // 6. 最终封装 - 绝不默认 audio/mpeg
    console.log("[MediaUploader:urlToFile] Final file", {
      finalName: fileName,
      finalType,
      finalSize: finalBlob.size,
    });
    return new File([finalBlob], fileName, { type: finalType });
  } catch (error) {
    console.error("Critical: urlToFile failed ->", error.message, {
      mediaType: props.mediaType,
      url,
      formats,
    });
    throw error;
  }
}

// Cleanup
onBeforeUnmount(() => {
  files.value.forEach((file) => releasePreviewUrl(file));
  onCropPointerUp();
  if (typeof window !== "undefined") {
    window.removeEventListener("resize", updateCropPreviewSize);
  }
  if (cropperState.sourceUrl) {
    URL.revokeObjectURL(cropperState.sourceUrl);
    cropperState.sourceUrl = "";
  }
  if (typeof cropperState._resolve === "function") {
    cropperState._resolve(null);
    cropperState._resolve = null;
  }
});

// Expose methods
defineExpose({
  clearFiles: () => {
    files.value.forEach((file) => releasePreviewUrl(file));
    files.value = [];
  },
  openFilePicker: () => {
    fileInputRef.value?.click();
  },
  addFromUrl: async (url) => {
    uploadState.url = url;
    await confirmUrlInput();
  },
});
</script>
